import { IndexValueType } from '@/types/apiTypes';

// Fixed categorical palette: colors are assigned per index when it is added and
// stay with it until removed, so the chart and map always agree. Ordered so
// neighbouring entries differ in both hue and lightness
const SERIES_COLORS = [
  '#357D42',
  '#1d4ed8',
  '#b45309',
  '#6d28d9',
  '#be123c',
  '#0e7490',
  '#84cc16',
  '#9d174d',
  '#f97316',
  '#0ea5e9',
  '#78350f',
  '#14b8a6',
  '#eab308',
  '#312e81',
  '#f43f5e',
  '#365314',
  '#86198f',
  '#64748b',
  '#0c4a6e',
  '#ec4899',
];

const MAX_COMPARE = SERIES_COLORS.length;

const homeTypeOptions = [
  { label: 'Bostadsrätt', value: 'apartment' },
  { label: 'Villa/radhus', value: 'house' },
];

const DEFAULT_CENTER = { lat: 59.33, lng: 18.0 };
const DEFAULT_ZOOM = 10;

interface SelectedIndexType {
  id: string;
  color: string;
}

const nextFreeColor = (selected: SelectedIndexType[]) =>
  SERIES_COLORS.find(
    (color) => !selected.some((item) => item.color === color)
  ) ?? SERIES_COLORS[0];

const valueDate = (value: IndexValueType) =>
  String(value.average_date).substring(0, 10);

const filterValuesByDateRange = (
  values: IndexValueType[],
  from: string,
  to: string
) =>
  values.filter((value) => {
    const date = valueDate(value);
    return (!from || date >= from) && (!to || date <= to);
  });

// Rebase a series so its earliest value equals baseValue — every later value
// then reads as the change since the start of the selected range
const rebaseValues = (
  values: IndexValueType[],
  baseValue: number
): IndexValueType[] => {
  if (!values.length || !baseValue) return values;

  const base = values.reduce(
    (earliest, value) =>
      valueDate(value) < valueDate(earliest) ? value : earliest,
    values[0]
  );
  if (!base.value) return values;

  const factor = baseValue / base.value;
  return values.map((value) => ({ ...value, value: value.value * factor }));
};

interface ChangeStatType {
  months: number;
  percent: number | null;
}

// Percent change from N months back to the latest value, for a monthly series
const computeChangeStats = (values: IndexValueType[]): ChangeStatType[] => {
  const sorted = [...values].sort((a, b) =>
    valueDate(a).localeCompare(valueDate(b))
  );
  const latest = sorted[sorted.length - 1];

  return [1, 3, 6, 12].map((months) => {
    const past = sorted[sorted.length - 1 - months];
    if (!latest?.value || !past?.value) return { months, percent: null };
    return { months, percent: (latest.value / past.value - 1) * 100 };
  });
};

export {
  SERIES_COLORS,
  MAX_COMPARE,
  homeTypeOptions,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  nextFreeColor,
  valueDate,
  filterValuesByDateRange,
  rebaseValues,
  computeChangeStats,
};
export type { SelectedIndexType, ChangeStatType };
